import { FunctionComponent, RefObject, useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";
import { useViewportClampedPanel } from "@/utils/useViewportClampedPanel";

interface MenuPosition {
	top: number;
	left: number;
	width: number;
}

interface SuggestionMenuProps {
	/** The chip box the menu hangs under. */
	anchorRef: RefObject<HTMLElement | null>;
	options: string[];
	/** Index of the keyboard-highlighted option, -1 while the pointer drives. */
	activeIndex?: number;
	/**
	 * The one option that is the user's own words rather than a taxonomy value —
	 * badged, so "nothing matched, but you can still save this" is visible instead
	 * of implied.
	 */
	customValue?: string;
	onSelect: (value: string) => void;
}

/**
 * The suggestion list of a chip input. It portals to the body with fixed
 * coordinates (web §11) — the panel body scrolls and clips, and inside the tour
 * overlay an in-flow menu would be cut off by the card it belongs to.
 */
export const SuggestionMenu: FunctionComponent<SuggestionMenuProps> = ({
	anchorRef,
	options,
	activeIndex = -1,
	customValue,
	onSelect,
}) => {
	const { t } = useTranslation();
	const [position, setPosition] = useState<MenuPosition | null>(null);
	const menuRef = useRef<HTMLUListElement>(null);

	// Keeps the portalled panel inside the viewport wherever the trigger sits (PREC-4138).
	useViewportClampedPanel(menuRef, !!position && options.length > 0);

	// Anchor the menu to the box, and follow it while the panel body scrolls.
	useEffect(() => {
		if (options.length === 0) {
			setPosition(null);

			return;
		}

		const place = () => {
			const rect = anchorRef.current?.getBoundingClientRect();

			if (rect) {
				setPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
			}
		};

		place();
		window.addEventListener("scroll", place, true);
		window.addEventListener("resize", place);

		return () => {
			window.removeEventListener("scroll", place, true);
			window.removeEventListener("resize", place);
		};
	}, [anchorRef, options.length]);

	if (!position || options.length === 0) {
		return null;
	}

	return createPortal(
		<ul
			ref={menuRef}
			// Above the tour shell (z-99990), below the in-tour dialogs (99995).
			className="fixed z-[99992] max-h-[220px] overflow-y-auto rounded-xl border border-border-light bg-white py-1 shadow-lg"
			style={{ top: position.top, left: position.left, minWidth: position.width }}
		>
			{options.map((option, index) => (
				<li key={option}>
					<button
						aria-selected={index === activeIndex}
						className={classNames(
							"flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-sm text-text hover:bg-background-light",
							index === activeIndex && "bg-background-light"
						)}
						type="button"
						onMouseDown={(event) => {
							// mousedown, not click: the input blurring first would unmount the menu.
							event.preventDefault();
							onSelect(option);
						}}
					>
						<span className="min-w-0 flex-1 truncate">{option}</span>
						{option === customValue && (
							<span className="shrink-0 rounded-full bg-blue-action-200 px-2 py-0.5 text-[10px] font-semibold text-brand-light">
								{t("onboardingTour.scoringStep.segments.addCustom")}
							</span>
						)}
					</button>
				</li>
			))}
		</ul>,
		document.body
	);
};
